//
// deprecated
// coreのrecordNodeUtilを使ってください
//

import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryList, ViewQueryNode, ViewQueryRecordNode } from '@salescore/core'
import { isNotNullish } from 'effect/Predicate'
import { v4 } from 'uuid'

import { VIEW_NEW_RECORD_PREFIX } from '../domain/constant'
import { getDefaultAttributes } from '../recoil/records/selectors/querySelector'
import { type DeepReadonly, toMutable } from '../rsheet/types/DeepReadonly'

// pathに応じたrecordsを取得する
// recordsは元のnodeのchildrenの配列オブジェクトそのものではなく、pushなどしても元のrootには反映されないので注意
// （深さ3以上のときを考えれば自明）
// eslint-disable-next-line sonarjs/todo-tag
// TODO: 返り値の型を ViewQueryRecordNode[] | undefined にする
/**
 * @deprecated Pure 版を使ってください
 */
// eslint-disable-next-line sonarjs/function-return-type
export function getRecordNodeFromRootNode(
  rootNode: ViewQueryRecordNode,
  nodePath: string[],
  // eslint-disable-next-line sonarjs/use-type-alias
): ViewQueryRecordNode | ViewQueryRecordNode[] | undefined {
  if (nodePath.length === 0) {
    return undefined
  }
  if (nodePath.length === 1) {
    return rootNode
  }
  return getRecordNode(rootNode, nodePath.slice(1))
}

export function getRecordNodeFromRootNodePure(
  rootNode: DeepReadonly<ViewQueryRecordNode>,
  nodePath: DeepReadonly<string[]>,
): ViewQueryRecordNode | ViewQueryRecordNode[] | undefined {
  // eslint-disable-next-line sonarjs/deprecation
  return getRecordNodeFromRootNode(toMutable(rootNode), toMutable(nodePath))
}

// eslint-disable-next-line sonarjs/function-return-type
function getRecordNode(
  node: ViewQueryRecordNode,
  nodePath: string[],
): ViewQueryRecordNode | ViewQueryRecordNode[] | undefined {
  if (nodePath.length === 0) {
    return node
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const childTableNode = node.children.find((x) => x.nodeName === nodePath[0]!)
  if (childTableNode === undefined) {
    return undefined
  }
  return childTableNode.children
    .flatMap((childRecordNode) => getRecordNode(childRecordNode, nodePath.slice(1)))
    .filter(isNotNullish)
}

/** @deprecated Pure 版を使ってください */
export function getNode(node: ViewQueryNode, nodePath: string[]): ViewQueryNode | undefined {
  if (nodePath.length === 1 && node.name === nodePath[0]) {
    return node
  }
  if (nodePath.length <= 1) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: ノードが見つからなかった時
    return undefined
  }
  const childNode = (node.children ?? []).find((childNode) => childNode.name === nodePath[1])
  if (childNode === undefined) {
    return undefined
  }

  // eslint-disable-next-line sonarjs/deprecation
  return getNode(childNode, nodePath.slice(1))
}

export function getNodePure(node: DeepReadonly<ViewQueryNode>, nodePath: readonly string[]): ViewQueryNode | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/deprecation
  return getNode(node as ViewQueryNode, nodePath as string[])
}

export function flatNodes<T extends { children?: readonly T[] }>(node: T | undefined): T[] {
  if (node === undefined) {
    return []
  }

  return [node, ...(node.children?.flatMap((childNode) => flatNodes(childNode)) ?? [])]
}

// treeのrootは削除できない仕様
export function removeNode({ tree, targetNodeName }: { tree: ViewQueryNode; targetNodeName: string }): ViewQueryNode {
  const children = tree.children ?? []

  if (children.length === 0) {
    return tree
  }

  if (children.map((x) => x.name).includes(targetNodeName)) {
    const newChildren = children.filter((x) => x.name !== targetNodeName)
    return {
      ...tree,
      children: newChildren,
    }
  }

  // dfs
  const newChildren = children.map((x) => removeNode({ tree: x, targetNodeName }))
  return {
    ...tree,
    children: newChildren,
  }
}

export function removeNodes({
  tree,
  targetNodeNames,
}: {
  tree: ViewQueryNode
  targetNodeNames: string[]
}): ViewQueryNode {
  const newNode = targetNodeNames.reduce(
    (currentTree, targetNodeName) => removeNode({ tree: currentTree, targetNodeName }),
    tree,
  )
  return newNode
}

export const generateNewRecordId = (): string => `${VIEW_NEW_RECORD_PREFIX}-${v4()}`

export const generateNewRootRecord = (query: DeepReadonly<Pick<ViewQueryList, 'tree' | 'fields'>>) => {
  // eslint-disable-next-line sonarjs/todo-tag
  const defaultAttributes = getDefaultAttributes(query.tree, query) // TODO: defaultAttributeはrecordChanges側で対応しないとrelationが反映されない
  const newRecord: ViewQueryRecordNode = {
    id: generateNewRecordId(),
    attributes: defaultAttributes.recordAttributes,
    children: [],
    meta: {
      height: 1,
      innerRowIndexStart: 0,
      innerRowIndexEnd: 1,
    },
  }
  const change: ViewRecordFieldChange | undefined =
    query.tree.write === undefined
      ? undefined
      : {
          id: newRecord.id,
          streamName: query.tree.write.streamName,
          fieldChanges: [], // デフォルト値に対しては不要なはず
          before: {},
          after: defaultAttributes.changeAttributes,
        }
  return {
    newRecord,
    change,
  }
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: RSheetと共通化？
/**
 * @deprecated Pure 版を使ってください
 */
export function getInnerRecordNode(
  recordNodes: ViewQueryRecordNode[],
  innerRowIndex: number,
): ViewQueryRecordNode | undefined {
  return recordNodes.find((x) => x.meta.innerRowIndexStart <= innerRowIndex && innerRowIndex < x.meta.innerRowIndexEnd)
}

export function getInnerRecordNodePure(
  recordNodes: DeepReadonly<ViewQueryRecordNode[]>,
  innerRowIndex: number,
): ViewQueryRecordNode | undefined {
  // eslint-disable-next-line sonarjs/deprecation
  return getInnerRecordNode(toMutable(recordNodes), innerRowIndex)
}
